// The technical status panel (Brief 5 / pt11) — "a technical status report that
// gets the user up to speed — direct and to the point". This is the babysitter's
// "catch me up technically" view, DISTINCT from the conversational operator chat:
// a compact, factual, scannable digest of the session's real state — connection +
// stream health, agent activity, recent read/write operations, any caught error +
// how it was handled, the open question(s) awaiting you, and the run/budget state.
//
// FACTS only (pt13): every value comes from `deriveSessionStatus` in @waytide/core
// (pure, tested) over the live session data the surface already holds — no
// predictions, no synthesized rationale. This component only RENDERS the digest:
// sentence-case labels, `font-code` for the technical values, the shared
// StreamHealthBadge for the connection, and the trace-tone → operator accent for
// each op's kind. It is SECONDARY by design — collapsed by default (a one-line
// summary stays useful while closed) so it never competes with the chat.
//
// Token-pure + a11y: a native disclosure (button + aria-expanded);
// read/write and the caught-error/outstanding states are encoded with WORDS, not
// colour alone; the reserved attention amber is used only for a genuine
// outstanding question (the one legitimate "this is on you"), and even then it
// does not animate.

import type {
  SessionStatusDigest,
  StreamHealth,
  TraceTone,
} from "@waytide/core";
import { tracePresentation } from "@waytide/core";
import { useState } from "react";
import { StreamHealthBadge } from "./StreamHealthBadge";
import { statusLabel } from "../model/presenceModel";
import "../styles/sessionStatus.css";

/** The trace tone → the operator accent class, so an op's kind reads in the same
 * accent the operator wears elsewhere (one vocabulary). The `arbiter` tone is
 * the three domain arbiters' SHARED hue — its class still carries the legacy
 * `watch-arbiter` token id until the design-token rename lands separately.
 * Neutral/success have no operator accent — they fall through to the default
 * text colour. */
const TONE_ACCENT: Partial<Record<TraceTone, string>> = {
  supervisor: "text-watch-supervisor",
  arbiter: "text-watch-arbiter",
  signal: "text-watch-signal",
};

/** A plain "$0.30" from server-computed micros (1e6 = $1.00). Display only — the
 * authoritative spend is always the ledger; this never sums money, only formats
 * a value already computed in core/the server. */
function money(micros: number): string {
  return `$${(Math.max(0, micros) / 1_000_000).toFixed(2)}`;
}

/** A short relative age from epoch-MICROS (the trace wire), for an op's time
 * slot — "now" / "5m" / "2h". `now` is ms; "" when the time is missing. */
function microsAge(atMicros: number, now: number): string {
  if (!Number.isFinite(atMicros) || atMicros <= 0) return "";
  const diffMs = now - atMicros / 1000;
  if (diffMs < 60_000) return "now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m`;
  return `${Math.floor(diffMs / 3_600_000)}h`;
}

/** The one-line glance shown while collapsed: the connection word + the agent
 * status, so the panel is useful before you open it. */
function glanceConnection(health: StreamHealth): string {
  return health === "live" ? "live" : health;
}

export interface SessionStatusPanelProps {
  /** The derived digest (from `deriveSessionStatus` in @waytide/core). */
  digest: SessionStatusDigest;
  /** Start expanded (default false — secondary, collapsed so it never competes
   * with the chat). */
  defaultOpen?: boolean;
  /** Injectable clock (ms) for deterministic tests; defaults to wall-clock. */
  now?: number;
}

export function SessionStatusPanel({
  digest,
  defaultOpen = false,
  now = Date.now(),
}: SessionStatusPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { connection, agent, recentOperations, caughtError, outstanding, budget } =
    digest;

  return (
    <section className="wt-status" data-open={open ? "true" : "false"} aria-label="Technical status">
      <button
        type="button"
        className="wt-status__summary"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="wt-status__caret" aria-hidden="true">
          ▸
        </span>
        <span className="wt-status__heading">Technical status</span>
        <span className="wt-status__glance">
          {/* The live connection cue, shared with the header so both speak once. */}
          <StreamHealthBadge
            health={connection.health}
            lastEventAt={connection.lastEventAt}
            now={now}
          />
          <span className="wt-status__muted font-code">
            {statusLabel(agent.status) || "—"}
          </span>
        </span>
      </button>

      {open ? (
        <div className="wt-status__body">
          {/* ── Connection ──────────────────────────────────────────────── */}
          <div className="wt-status__section">
            <span className="wt-status__label">Connection</span>
            <div className="wt-status__row">
              <span className="wt-status__key">stream</span>
              <span className="wt-status__val font-code">
                {glanceConnection(connection.health)}
                {connection.lastFrameAge
                  ? ` · last frame ${connection.lastFrameAge}`
                  : " · no frame yet"}
              </span>
            </div>
          </div>

          {/* ── Agent activity ──────────────────────────────────────────── */}
          <div className="wt-status__section">
            <span className="wt-status__label">Agent</span>
            <div className="wt-status__row">
              <span className="wt-status__key">state</span>
              <span className="wt-status__val font-code">
                {statusLabel(agent.status) || "unknown"}
              </span>
            </div>
            {agent.harness || agent.model ? (
              <div className="wt-status__row">
                <span className="wt-status__key">runs</span>
                <span className="wt-status__val font-code">
                  {[agent.harness, agent.model].filter(Boolean).join(" · ")}
                </span>
              </div>
            ) : null}
            <div className="wt-status__row">
              <span className="wt-status__key">last op</span>
              <span className="wt-status__val font-code">
                {agent.lastOperation
                  ? `${agent.lastOperation.label} — ${
                      agent.lastOperation.summary || "(no summary)"
                    }`
                  : "none yet"}
              </span>
            </div>
          </div>

          {/* ── Recent operations (read / write) ────────────────────────── */}
          <div className="wt-status__section">
            <span className="wt-status__label">Recent operations</span>
            {recentOperations.length === 0 ? (
              <span className="wt-status__muted">No operations yet.</span>
            ) : (
              <ul className="wt-status__ops">
                {recentOperations.map((op, i) => {
                  const accent = TONE_ACCENT[tracePresentation(op.kind).tone];
                  return (
                    <li
                      key={`${op.kind}-${op.atMicros}-${i}`}
                      className="wt-status__op"
                      data-write={op.write ? "true" : "false"}
                    >
                      <span className={`wt-status__op-kind font-code${accent ? ` ${accent}` : ""}`}>
                        {op.label}
                      </span>
                      <span className="wt-status__op-summary">
                        {op.summary || "(no summary)"}
                      </span>
                      <span className="wt-status__tag">
                        {op.write ? "write" : "read"}
                        {microsAge(op.atMicros, now)
                          ? ` · ${microsAge(op.atMicros, now)}`
                          : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ── Caught error + how handled ──────────────────────────────── */}
          {caughtError ? (
            <div className="wt-status__section">
              <span className="wt-status__label">Caught</span>
              <div className="wt-status__caught">
                <div className="wt-status__caught-head">
                  <span className="wt-status__caught-kind font-code">
                    {caughtError.label}
                  </span>
                  <span className="wt-status__caught-handled">
                    {caughtError.handled}
                  </span>
                </div>
                <span className="wt-status__caught-summary">
                  {caughtError.summary || "(no summary)"}
                </span>
              </div>
            </div>
          ) : null}

          {/* ── Outstanding question(s) ─────────────────────────────────── */}
          {outstanding.length > 0 ? (
            <div className="wt-status__section">
              <span className="wt-status__label">Awaiting you</span>
              <ul className="wt-status__open">
                {outstanding.map((q, i) => (
                  <li key={`${q.source}-${i}`} className="wt-status__q">
                    <span className="wt-status__q-prompt">{q.prompt}</span>
                    <span className="wt-status__q-meta">
                      {q.source === "steering"
                        ? `steering decision · ${q.optionCount} ${
                            q.optionCount === 1 ? "option" : "options"
                          }`
                        : "session reported it needs you"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* ── Run / budget ────────────────────────────────────────────── */}
          {budget ? (
            <div className="wt-status__section">
              <span className="wt-status__label">Budget</span>
              <div
                className="wt-status__budget"
                data-exhausted={budget.exhausted ? "true" : "false"}
              >
                <span className="wt-status__key">spent</span>
                <span className="wt-status__val font-code">
                  {money(budget.spentMicros)}
                  {budget.capMicros !== null
                    ? ` of ${money(budget.capMicros)}`
                    : ""}
                  {budget.exhausted ? " · exhausted" : ""}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
